import { useTranslation } from "react-i18next"
import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"
import { PodClass } from "../../../classes/Pod"
import { useStore } from "../../../contexts/store"
import { iTag } from "../../../shared/src/types/Interaction"
import { Tag } from "../../../shared/src/types/Content"

import { Accordion, AccordionDetails, AccordionSummary } from "../../Elements/Accordion"
import Blockquote from "../../Elements/Blockquote"
import DeleteTagDialog from "./DeleteTagDialog"
import InteractionIcon from "../../Elements/InteractionIcon"
import Link from "../../Elements/Link"
import TagSettingsDialog from "./TagSettingsDialog"
import UxIcon from "../../Elements/UxIcon"

import { Box, Chip, Paper, ToggleButton, ToggleButtonGroup } from "@mui/material"


const PodTags = ({pod}: {pod: PodClass}) => {
  const { t } = useTranslation()
  const { podStore, sessionStore, uiStore } = useStore()
  const [tags, setTags] = useState<Tag[]>([])
  const deviceWidth = uiStore.deviceWidth
  const tagNames = pod.getTagNames()
  const numberOfTags = tagNames.length
  const tagSortOrder = uiStore.tagSortOrder
  const tagColor = uiStore.getInteractionColor("tagging")

  useEffect(()=> {
    const sortedTags = pod.getSortedTags(tagSortOrder)
    setTags(sortedTags)
  }, [numberOfTags, tagSortOrder, pod, pod.getSortedTags])

  // scroll to url hash
  useEffect(() => {
    const hash = window.location.hash.substring(1)
    if(hash) {
      const element = document.getElementById(hash)
      if(element) {
        // scroll to hash element
        element.scrollIntoView({ behavior: "smooth" })
        // remove hash from url
        window.location.hash = ""
      }
    }
  })

  if (!sessionStore.session) return null

  const userId = sessionStore.session.user.userId
  const allowedAddTag = isAllowed(pod, 'addTag', userId)
  const allowedEditTag = isAllowed(pod, 'editTag', userId)
  const allowedDeleteTag = isAllowed(pod, 'deleteTag', userId)

  let paddingHeader = "20px"
  if(deviceWidth === "smaller") paddingHeader = "25px"
  if(deviceWidth === "small")   paddingHeader = "30px"
  if(deviceWidth === "medium")  paddingHeader = "40px"
  if(deviceWidth === "large")   paddingHeader = "50px"
  if(deviceWidth === "xlarge")  paddingHeader = "50px"


  function handleToggleSort(newAlignment: string) {
    if(newAlignment && newAlignment !== tagSortOrder) {
      setTags([])
    }
    uiStore.setTagSortOrder(newAlignment ? newAlignment : "alphabetical")
  }

  const Tagging = ({tagging}: {tagging: iTag}) => {
    const filename = tagging.anchor.nodeId ? podStore.getFilename(tagging.anchor.nodeId) : ""
    const pageNumber = tagging.anchor.rects.length ?  tagging.anchor.rects[0].p : ""
    const text = tagging.anchor.relText ? tagging.anchor.relText : `( ${t('There is no text available')} )`
    return (
      <Link noDecoration={true} to={`/pod/${pod.podId}/pdf/${tagging.anchor.nodeId}/annotation/${tagging.interactionId}`}>
        <Paper elevation={3} sx={{display: "grid", p: "20px", m: "20px 15px", "&:hover": {backgroundColor: "whitesmoke"} }}>
          <Box sx={{
            color: "textcolor.light",
            display: "flex",
            fontSize: "13px",
            justifyContent: "end",
            padding: "5px 10px 0 10px",
            wordBreak: "break-word"
          }}>
            {filename}: <span style={{fontWeight: 450, paddingLeft: "3px", wordBreak: "keep-all"}}>{pageNumber}</span>
          </Box>
          <Blockquote color={tagColor} hasToggleButton={false} text={text} />
        </Paper>
      </Link>
    )
  }

  const TagAccordion = observer(({tag}: {tag: Tag}) => {
    const taggings = pod.getTaggingsWithTagId(tag.tagId)
    const expanded = (uiStore.tagIsOpen[pod.podId] && uiStore.tagIsOpen[pod.podId][tag.tagId]) ? true : false

    function handleExpandTag() {
      uiStore.setTagIsOpen(pod.podId, tag.tagId, !expanded)
    }

    return (
      <Accordion id={tag.tagId} expanded={expanded} onChange={handleExpandTag}>
        <AccordionSummary component={"div"} sx={{backgroundColor: expanded ? "#f9f4f4" : "white"}}>
          <Box sx={{
            alignItems: "center",
            backgroundColor: expanded ? "#f9f4f4" : "white",
            display: "flex",
            flexWrap: "wrap",
            flexGrow: 1,
            gap: "15px",
            justifyContent: "start",
            padding: "5px 0",
            wordBreak: "break-word"
          }}>
            <Box sx={{fontSize: "18px", paddingLeft: "10px"}}>
              {taggings.length}x
            </Box>
            <Chip
              icon={<InteractionIcon fontSize="small" interactionType="tagging" />}
              label={`# ${tag.name}`}
              sx={{
                borderColor: tagColor,
                color: tagColor,
                fontSize: "17px",
                padding: "10px"
              }}
              variant="outlined"
            />
            <Box sx={{
              display: "grid",
              flexGrow: 1,
              gridTemplateColumns: "max-content max-content",
              justifyContent: "end"
            }}>
              {allowedDeleteTag &&
                <DeleteTagDialog podId={pod.podId} tagId={tag.tagId} tagName={tag.name} />
              }
              {allowedEditTag &&
                <TagSettingsDialog  podId={pod.podId} tag={tag} tagNames={tagNames} />
              }

            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{
          backgroundColor: "#f7f7f78a"
        }}>
          {taggings.map(tagging =>
            <li key={tagging.interactionId} style={{listStyle: "none"}}>
              <Tagging tagging={tagging} />
            </li>
          )}
        </AccordionDetails>
      </Accordion>
    )
  })

  const ToggleSorting = () => (
      <ToggleButtonGroup
        value={tagSortOrder}
        exclusive
        onChange={(_, newAlignment: string) => {handleToggleSort(newAlignment)}}
        aria-label="tag sorting by alphabet or frequency"
        size="small"
      >
        <ToggleButton size="small" sx={{display: "flex", flexWrap: "wrap", gap: "15px"}} value={"alphabetical"}>
          <UxIcon id="sortByAlphabet" />
          {t('Alphabetical')}
        </ToggleButton>
        <ToggleButton size="small" sx={{display: "flex", flexWrap: "wrap", gap: "15px"}}value={"frequency"}>
          <UxIcon id="sortByFrequency" />
          {t('Frequency')}
        </ToggleButton>
      </ToggleButtonGroup>
  )

  return (
    <Box>
      <Box sx={{
        color: "textcolor.main",
        fontSize: "20px",
        fontWeight: 600,
        padding: `0 ${paddingHeader}`,
        wordBreak: "break-word"
      }}>
        {t('{{numberOfTags}} tags in this Pod', {numberOfTags: numberOfTags})}
        <Box sx={{margin: "10px 0"}}>
          <ToggleSorting />
        </Box>
      </Box>
      {allowedAddTag &&
        <TagSettingsDialog podId={pod.podId} tag={null} tagNames={tagNames} />
      }
      <Box sx={{
        margin: "20px 0"
      }}>
        {tags.map(tag =>
          <TagAccordion key={tag.tagId} tag={tag} />
        )}
      </Box>
    </Box>
  )
}

export default observer(PodTags)

function isAllowed(pod: PodClass, opCode: string, userId: number) {
  let isAllowed = false

  if(userId) {
    if(pod.checkForRole('Admin')) {
      const adminUsergroupId = pod.getUsergroupByRole('Admin').usergroupId
      const adminGroup = pod.usergroups[adminUsergroupId]
      if(adminGroup.permissions.indexOf(opCode)>-1) {
        isAllowed = true
      }
    }

    if(pod.checkForRole('Pod')) {
      const podUsergroupId = pod.getUsergroupByRole('Pod').usergroupId
      const podGroup = pod.usergroups[podUsergroupId]
      if(podGroup.permissions.indexOf(opCode)>-1) {
        isAllowed = true
      }
    }
  }

  return isAllowed
}
